// import '@babel/polyfill'
import Vue from 'vue'
import vuetify from './plugins/vuetify'
import App from './App.vue'
import router from './router'
import store from './store'
import { mapGetters } from 'vuex'
import './plugins/axios'
import i18n from './plugins/i18n/i18n'
import './plugins/vuelidate'
import DateFilter from './plugins/formatters/date'
import moment from 'moment'
import { required, requiredIf, email, decimal, minValue, helpers } from 'vuelidate/lib/validators'
import IBAN from 'iban'
import goTo from 'vuetify/lib/services/goto'
import ItemGroup from './components/ItemGroup'
import InputGroup from './components/InputGroup'

import VueCookies from 'vue-cookies'

const validateIf = (prop, validator) =>
  helpers.withParams({ type: 'validatedIf', prop }, function (value, parentVm) {
    return helpers.ref(prop, this, parentVm) ? validator(value) : true
  })
const socSec = (value) => value !== '' ? /^(0[1-9]|[12]\d|3[01])(0[1-9]|1[0-2])([5-9]\d\+|\d\d[-U-Y]|[012]\d[A-F])\d{3}[\dA-Z]$/.test(value) : true
const validIBAN = (value) => IBAN.isValid(value)
const businessId = helpers.regex('businessId', /^([0-9]{8}|[0-9]{7}-[0-9]{1})$/)
const postalNumber = helpers.regex('postalNumber', /^[0-9]{5}$/)
const phoneNumber = helpers.regex('phoneNumber', /^(\+[0-9]{3}[0-9]{7,10}|[0-9]{8,11})$/)

function underageCreditor (value) {
  let validSSN = /^(0[1-9]|[12]\d|3[01])(0[1-9]|1[0-2])([5-9]\d\+|\d\d[-U-Y]|[012]\d[A-F])\d{3}[\dA-Z]$/.test(value)
  let ageInYears = 0

  let divider = value.substring(6, 7)
  let century = 19

  if (divider == 'A' || divider == 'B' || divider == 'C' || divider == 'D' || divider == 'E' || divider == 'F') {
    century = 20
  }

  let DOB = value.substring(0, 4) + century + value.substring(4, 6);

  if (value.length >= 6) {
    ageInYears = moment().diff(moment(DOB, "DDMMYYYY"), 'years')
  }

  if (validSSN && ageInYears < 18) {
    return false
  } else {
    return true
  }
}

Vue.config.productionTip = false

Vue.filter('formatDate', DateFilter)

Vue.component('CustomItemGroup', ItemGroup)
Vue.component('CustomInputGroup', InputGroup)

Vue.use(VueCookies)

new Vue({
  vuetify,
  i18n,
  router,
  store,
  computed: {
    ...mapGetters(['locale', 'order', 'client', 'bills', 'debtors', 'countries', 'moneyLaundering', 'companyRepresentative', 'signing']),
    BICIsRequired () {
      return this.client.IBAN !== '' &&
        this.client.IBAN.length > 2 &&
        this.client.IBAN.toUpperCase().substring(0, 2) !== 'FI'
    },
    dialogSettings () {
      return {
        fullscreen: this.$vuetify.breakpoint.smAndDown,
        scrollable: false,
        hideoverlay: this.$vuetify.breakpoint.smAndDown,
        transition: this.$vuetify.breakpoint.smAndDown ? 'dialog-bottom-transition' : 'dialog-transition',
        width: this.$vuetify.breakpoint.smAndDown ? 'auto' : '1024px',
        maxwidth: this.$vuetify.breakpoint.smAndDown ? 'none' : '90%'
      }
    }
  },
  watch: {
    locale: function (val) {
      this.$i18n.locale = val

      let d = new Date()
      d.setTime(d.getTime() + (1 * 24 * 60 * 60 * 1000))

      let expires = 'expires=' + d.toUTCString()
      document.cookie = 'CultureInfo=' + this.$store.getters.longLocale + ';' + expires + ';path=/'
    },
    '$v.order.basicInformation.$anyDirty': function (val) {
      this.$store.commit('updateOrder', { value: val, prop: 'isDirty' })
    },
    '$v.order.basicInformation.$anyError': function (val) {
      this.$store.commit('updateOrder', { value: !val, prop: 'isValid' })
    },
    '$v.client.$anyDirty': function (val) {
      this.$store.commit('updateClient', { value: val, prop: 'isDirty' })
    },
    '$v.client.$anyError': function (val) {
      this.$store.commit('updateClient', { value: !val, prop: 'isValid' })
    },
    '$v.moneyLaundering.$anyDirty': function (val) {
      this.$store.commit('updateMoneyLaundering', { value: val, prop: 'isDirty' })
    },
    '$v.moneyLaundering.$invalid': function (val) {
      this.$store.commit('updateMoneyLaundering', { value: !val, prop: 'isValid' })
    },
    '$v.signing.$anyDirty': function (val) {
      this.$store.commit('updateSigning', { value: val, prop: 'isDirty' })
    },
    '$v.signing.$anyError': function (val) {
      this.$store.commit('updateSigning', { value: !val, prop: 'isValid' })
    }
  },
  created () {
    if (this.$cookies.isKey('CultureInfo')) {
      this.$store.commit('setLocale', this.$cookies.get('CultureInfo'))
    } else {
      this.$store.commit('setLocale', 'fi')
    }

    this.$i18n.locale = this.locale
  },
  methods: {
    scrollTo (target) {
      this.$nextTick(() => {
        if (target && document.querySelector(target)) {
          goTo(target)
        }
      })
    },
    addNewBill () {
      this.$store.dispatch('addBill')
      this.scrollTo('#bill-' + this.bills.length)
    },
    removeBill (index) {
      this.$store.dispatch('removeBill', index)
    },
    updateBill (prop, val, index) {
      this.$store.commit('updateBill', { index: index, value: val, prop: prop })
      this.$store.commit('updateBill', { index: index, value: !this.$v.order.bills.$each[index].$anyError, prop: 'isValid' })
      this.$store.commit('updateBill', { index: index, value: this.$v.order.bills.$each[index].$anyDirty, prop: 'isDirty' })
    },
    addNewDebtor () {
      this.$store.dispatch('addDebtor')
      this.scrollTo('#debtor-' + this.debtors.length)
    },
    removeDebtor (index) {
      this.$store.dispatch('removeDebtor', index)
    },
    updateDebtor (prop, val, index) {
      this.$store.commit('updateDebtor', { index: index, value: val, prop: prop })
      this.$store.commit('updateDebtor', { index: index, value: !this.$v.order.debtors.$each[index].$anyError, prop: 'isValid' })
      this.$store.commit('updateDebtor', { index: index, value: this.$v.order.debtors.$each[index].$anyDirty, prop: 'isDirty' })
    },
    updateOrder (prop, val) {
      this.$store.commit('updateOrder', { value: val, prop: prop })
    },
    updateClient (prop, val) {
      this.$store.commit('updateClient', { value: val, prop: prop })
    },
    updateContactPerson (prop, val) {
      this.$store.commit('updateContactPerson', { value: val, prop: prop })
    },
    updateCompanyRepresentative (prop, val) {
      this.$store.commit('updateCompanyRepresentative', { value: val, prop: prop })
    },
    updateMoneyLaundering (prop, val) {
      this.$store.commit('updateMoneyLaundering', { value: val, prop: prop })
    },
    updateSigning (prop, val) {
      this.$store.commit('updateSigning', { value: val, prop: prop })
    }
  },
  render: h => h(App),
  validations () {
    return {
      order: {
        basicInformation: {
          claimType: {
            required
          },
          customerNr: {
            required: false
          },
          information: {
            required: false
          }
        },
        bills: {
          $each: {
            billNr: {
              required: requiredIf((self) => {
                return self.information === ''
              })
            },
            amount: {
              required,
              decimal,
              minValue: minValue(5)
            },
            information: {
              required: requiredIf((self) => {
                return self.billNr === '' || this.order.basicInformation.claimType === 14
              })
            },
            referenceNr: {
              required: false
            },
            billDate: {
              required: false
            },
            dueDate: {
              required
            },
            interestType: {
              required: requiredIf(() => {
                return this.debtors[0].isCompany
              })
            },
            interest: {
              decimal,
              required: requiredIf((self) => {
                return this.debtors[0].isCompany && self.interestType === 1
              })
            }
          }
        },
        debtors: {
          $each: {
            companyName: {
              required: requiredIf((self) => {
                return self.isCompany
              })
            },
            companyBusinessId: {
              required: requiredIf((self) => {
                return self.isCompany
              }),
              businessId: validateIf((self) => { return self.isCompany }, businessId)
            },
            firstName: {
              required: requiredIf((self) => {
                return !self.isCompany
              })
            },
            lastName: {
              required: requiredIf((self) => {
                return !self.isCompany
              })
            },
            socSecNr: {
              socSec: validateIf((self) => { return !self.isCompany }, socSec)
            },
            language: {
              required: requiredIf((self) => {
                return !self.isCompany
              })
            },
            streetAddress: {
              required
            },
            postalNumber: {
              required,
              postalNumber
            },
            city: {
              required
            },
            phone: {
              required: false,
              phoneNumber
            },
            email: {
              required: false,
              email
            },
            country: {
              required
            },
            isCompany: {
              required
            }
          }
        }
      },
      client: {
        isCompany: {
          required
        },
        firstName: {
          required: requiredIf((self) => {
            return !self.isCompany
          })
        },
        lastName: {
          required: requiredIf((self) => {
            return !self.isCompany
          })
        },
        businessId: {
          required: requiredIf((self) => {
            return self.isCompany
          }),
          businessId: this.client.isCompany ? businessId : true
        },
        companyName: {
          required: requiredIf((self) => {
            return self.isCompany
          })
        },
        companyType: {
          required: requiredIf((self) => {
            return self.isCompany
          })
        },
        address: {
          required
        },
        postalNumber: {
          required,
          postalNumber
        },
        city: {
          required
        },
        phoneNumber: {
          required: requiredIf((self) => {
            return !self.isCompany
          }),
          phoneNumber: this.client.isCompany ? true : phoneNumber
        },
        email: {
          required: requiredIf((self) => {
            return !self.isCompany
          }),
          email: this.client.isCompany ? true : email
        },
        socSecNr: {
          required: requiredIf((self) => {
            return !self.isCompany
          }),
          socSec: this.client.isCompany ? true : socSec,
          underageCreditor: this.client.isCompany ? true : underageCreditor
        },
        IBAN: {
          required,
          validIBAN
        },
        BIC: {
          required: requiredIf(() => {
            return this.BICIsRequired
          })
        },
        contactPerson: {
          email: {
            email,
            required: requiredIf(() => {
              return this.client.isCompany
            })
          },
          firstName: {
            required: requiredIf(() => {
              return this.client.isCompany
            })
          },
          lastName: {
            required: requiredIf(() => {
              return this.client.isCompany
            })
          },
          phoneNumber: {
            required: requiredIf(() => {
              return this.client.isCompany
            }),
            phoneNumber: this.client.isCompany ? phoneNumber : true
          }
        },
        vatObligation: {
          required: requiredIf(() => {
            return this.client.isCompany
          })
        }
      },
      moneyLaundering: {
        typicalClaim: {
          required
        },
        capitalClassification: {
          required
        },
        politicallyInfluentialPerson: {
          required
        },
        politicallyInfluentialPersonStatus: {
          required: requiredIf((self) => {
            return self.politicallyInfluentialPerson
          })
        },
        foreignGroupCompany: {
          required: requiredIf(() => {
            return this.client.isCompany
          })
        },
        foreignGroupCompanyPlace: {
          required: requiredIf((self) => {
            return this.client.isCompany && self.foreignGroupCompany === 2
          })
        },
        needsOperatingLicense: {
          required: requiredIf(() => {
            return this.client.isCompany
          })
        },
        operatingLicenseNameAndAuthority: {
          required: requiredIf((self) => {
            return this.client.isCompany && self.needsOperatingLicense
          })
        },
        hasNotificationObligation: {
          required: requiredIf(() => {
            return this.client.isCompany
          })
        },
        hasBigShareholders: {
          required: requiredIf((self) => {
            return this.client.isCompany
          })
        },
        hasPublicStockExchangeInEUCountry: {
          required: requiredIf((self) => {
            return this.client.isCompany &&
              self.hasBigShareholders
          })
        },
        publicStockExchangeInEUCountry: {
          required: requiredIf((self) => {
            return this.client.isCompany &&
              self.hasBigShareholders &&
              self.hasPublicStockExchangeInEUCountry
          })
        },
        shareholders: {
          required: requiredIf((self) => {
            return this.client.isCompany &&
              self.hasBigShareholders &&
              !self.hasPublicStockExchangeInEUCountry
          })
        },
        companyRepresentative: {
          isRepresentative: {
            required: requiredIf(() => {
              return this.client.isCompany
            })
          },
          file: {
            required: requiredIf((self) => {
              return this.client.isCompany && self.isRepresentative === 'powerOfAttorney'
            })
          },
          positionInCompany: {
            required: requiredIf((self) => {
              return this.client.isCompany && self.isRepresentative === 'position'
            })
          }
        }
      },
      signing: {
        contractAgreement: {
          required,
          checked: value => value === true
        },
        firstName: {
          required: requiredIf((self) => {
            return this.client.isCompany
          })
        },
        lastName: {
          required: requiredIf((self) => {
            return this.client.isCompany
          })
        },
        socSecNr: {
          required: requiredIf(() => {
            return this.client.isCompany
          }),
          socSec: this.client.isCompany ? socSec : true,
          underageCreditor: this.client.isCompany ? underageCreditor : true
        }
      }
    }
  }
}).$mount('#app')
